import React from 'react';

import { calculatePercentage } from '@app/containers/components/OccupancyRate';
import { Colors } from '@blueprintjs/core';

import {
    Chart,
    ChartBar,
    ChartBarLabel,
    ChartGridLine,
    ChartGroup,
    ChartLabels,
    Legend,
    ScrollArea,
    Swatch,
} from '../../styled';
import { TeamHealthWeek, formatCeilPercentage } from '../../utils/teamHealth';

export interface WeeklyChartTeam {
    id: string;
    title: string;
    color: string;
}

export interface WeeklyChartProps {
    teams: WeeklyChartTeam[];
    weeks: TeamHealthWeek[];

    // Score per week (same order as weeks), keyed by team ID; absent when a team has no record
    scores: Array<Record<string, number>>;

    // Accessible name of the chart; the Quarterly view groups by quarter instead
    ariaLabel?: string;
}

const GRID_STEP = 0.2;

// Vertical bars grouped by week, one bar per team in the team's own color
const WeeklyChart: React.FC<WeeklyChartProps> = ({ teams, weeks, scores, ariaLabel = 'Team occupancy by week' }) => {
    const maxScore = Math.max(1.2, ...scores.flatMap((weekScores) => Object.values(weekScores)));
    const top = Math.ceil(maxScore / GRID_STEP) * GRID_STEP;
    const gridLines: number[] = [];
    for (let value = 0; value <= top + 1e-9; value += GRID_STEP) {
        gridLines.push(value);
    }

    // Keep bars readable: horizontal scroll rather than squeezing many weeks into a narrow page
    const minWidth = 44 + weeks.length * Math.max(90, teams.length * 14 + 16);

    return (
        <>
            <ScrollArea>
                <Chart $minWidth={minWidth} role="img" aria-label={ariaLabel}>
                    {gridLines.map((value) => (
                        <ChartGridLine key={value} $bottom={(value / top) * 100}>
                            <span>{Math.round(value * 100)}%</span>
                        </ChartGridLine>
                    ))}
                    {weeks.map((week, weekIndex) => (
                        <ChartGroup key={week.fromDate}>
                            {teams.map((team) => {
                                const score = scores[weekIndex]?.[team.id];
                                if (score === undefined) {
                                    return null;
                                }

                                return (
                                    <ChartBar
                                        key={team.id}
                                        $height={(score / top) * 100}
                                        $color={team.color || Colors.GRAY3}
                                        title={`${team.title} · ${week.label}: ${calculatePercentage(score)}%`}
                                    >
                                        <ChartBarLabel>{formatCeilPercentage(score)}</ChartBarLabel>
                                    </ChartBar>
                                );
                            })}
                        </ChartGroup>
                    ))}
                </Chart>
                <ChartLabels $minWidth={minWidth}>
                    {weeks.map((week) => (
                        <span key={week.fromDate}>{week.label}</span>
                    ))}
                </ChartLabels>
            </ScrollArea>
            <Legend>
                {teams.map((team) => (
                    <span key={team.id}>
                        <Swatch $color={team.color || Colors.GRAY3} />
                        {team.title}
                    </span>
                ))}
            </Legend>
        </>
    );
};

export default WeeklyChart;
